<!--
  createResponsibleDialog ("Ansprechpartner:in anlegen") as numbered steps (CreateStepperDialog) around the shared
  responsibleForm (also used by editResponsibleDialog). Same request and payload as before (ResponsibleHandlerAPI.create with
  assignToInstitution), same events (create:responsible + EventBus) and job type guard.
  Props: value, institutionId, institutionName. Events: input, create:responsible
-->
<template>
  <create-stepper-dialog ref="shell" :dirty="isDirty" :loading="loading" :max-width="900" :sections="sections" :subtitle="subtitle"
                         :value="value" icon="mdi-account-plus-outline" save-text="Hinzufügen" title="Ansprechpartner:in anlegen"
                         @close="reset" @save="createResponsible">
    <v-form ref="form" v-model="valid" lazy-validation @submit.prevent="createResponsible">
      <responsible-form v-model="responsible" :disabled="loading" :job-types="jobTypes" :job-types-loading="jobTypesLoading"
                        :permissions="permissions" @job-type-created="addJobType"/>
    </v-form>
  </create-stepper-dialog>
</template>

<script lang="ts">
import {GenderTypes} from '@/enums/GenderTypes.enum';
import Vue from 'vue';
import {Form} from '@/interfaces/Form.interface';
import {Types} from 'mongoose';
import {CreateResponsibleRequestDTO} from '@/classes/dto/responsible-handler/request/CreateResponsible.request.dto';
import {ResponsibleHandlerAPI} from '@/classes/api/responsible-handler.api.class';
import {EventBus} from '@/busses/EventBus';
import {JobType} from '@/interfaces/jobType.interface';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {CrudActionTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudActionTypes';
import {contactFormToCreateDto} from '@/helpers/contactInformationForm.helper';
import ResponsibleForm from '@/components/responsibles/responsibleForm.vue';
import CreateStepperDialog, {CreateStepperSection} from '@/components/common/modern/createStepperDialog.vue';
import {
  emptyResponsibleForm,
  JobTypeItem,
  loadJobTypeItems,
  ResponsibleFormPermissions,
  ResponsibleFormValue,
} from '@/components/responsibles/responsibleForm.helper';

export default Vue.extend({
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    institutionId: {
      type: Types.ObjectId,
      required: true,
    },
    /** Optional name of the institution for the subtitle */
    institutionName: {
      type: String,
      default: '',
    },
  },
  components: {CreateStepperDialog, ResponsibleForm},
  data: () => ({
    valid: true,
    responsible: emptyResponsibleForm() as ResponsibleFormValue,
    jobTypes: [] as JobTypeItem[],
    jobTypesLoading: false,
    loading: false,
  }),
  computed: {
    subtitle(): string {
      // The former footer legend "* Pflichtfeld" lives in the subtitle now (the stepper footer shows "Weiter: …")
      const target = this.institutionName ? `Neue Kontaktperson für ${this.institutionName}` : 'Neue Kontaktperson für diesen Standort';
      return `${target} · Pflichtfelder mit *`;
    },
    permissions(): Partial<ResponsibleFormPermissions> {
      return {createJobType: this.$$crudAccessManager.getAccess(CrudEntityTypes.JOB_TYPE, CrudActionTypes.CREATE)};
    },
    createDto(): CreateResponsibleRequestDTO {
      return {
        createLogin: false,
        birthday: this.responsible.birthday ? this.responsible.birthday : undefined,
        contactInformation: contactFormToCreateDto(this.responsible.contactInformation),
        jobType: this.responsible.jobType ? this.responsible.jobType : undefined,
        firstName: this.responsible.firstName,
        lastName: this.responsible.lastName,
        gender: this.responsible.gender as GenderTypes,
        assignToInstitution: this.institutionId,
      };
    },
    /** Steps = the sections of responsibleForm (all field groups are shown when creating) */
    sections(): CreateStepperSection[] {
      const r = this.responsible;
      return [
        {id: 'person', title: 'Person', icon: 'mdi-account-outline', complete: r.gender !== null && !!r.lastName.trim()},
        {id: 'position', title: 'Position', icon: 'mdi-briefcase-outline', hint: 'optional'},
        {id: 'contact', title: 'Kontakt', icon: 'mdi-card-account-phone-outline', hint: 'optional'},
      ];
    },
    isDirty(): boolean {
      return JSON.stringify(this.responsible) !== JSON.stringify(emptyResponsibleForm());
    },
  },
  methods: {
    async loadJobTypes() {
      this.jobTypesLoading = true;
      try {
        this.jobTypes = await loadJobTypeItems();
      } finally {
        this.jobTypesLoading = false;
      }
    },
    addJobType(jobType: JobType) {
      this.jobTypes = [...this.jobTypes, {text: jobType.neutral, value: jobType._id}];
    },
    async createResponsible() {
      const form = this.$refs.form as Form;
      if (!form.validate()) {
        (this.$refs.shell as InstanceType<typeof CreateStepperDialog> | undefined)?.scrollToFirstError();
        return;
      }
      try {
        this.loading = true;
        const response = await ResponsibleHandlerAPI.create(this.createDto);
        this.$emit('create:responsible', response);
        EventBus.$emit('create:responsible');
        this.$$showSnackbar('Ansprechpartner:in wurde erfolgreich angelegt.', 'success');
        this.reset();
      } finally {
        this.loading = false;
      }
    },
    /** Explicit start values (no form.reset()) */
    reset() {
      this.responsible = emptyResponsibleForm();
      const form = this.$refs.form as Form | undefined;
      if (form) {
        form.resetValidation();
      }
      this.valid = true;
      this.$emit('input', false);
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(v: boolean) {
        if (v && this.jobTypes.length === 0) {
          this.loadJobTypes();
        }
      },
    },
  },
});
</script>
